import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/movie";
import { getMovieById, getRelated, categoryLabel } from "@/lib/movieList";
import MovieCard from "@/components/feature/MovieCard";
import ShareDialog from "@/pages/detail/components/ShareDialog";

const list = movies as Movie[];

export default function Detail() {
  const { id } = useParams();
  const movie = useMemo(() => getMovieById(list, id ?? ""), [id]);
  const [currentEpisode, setCurrentEpisode] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setCurrentEpisode(1);
  }, [id]);

  useEffect(() => {
    if (movie) document.title = `${movie.title} - ${categoryLabel(movie.category)}详情 - 97影院`;
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被下架，或链接有误。</p>
        <Link
          to="/category"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回分类页
        </Link>
      </div>
    );
  }

  const related = getRelated(list, movie);

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="relative h-[280px] w-full overflow-hidden md:h-[400px]">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} ${movie.genre} - 97影院`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/30" />
        </div>

        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <div className="relative -mt-28 flex flex-col gap-6 md:-mt-40 md:flex-row md:gap-8">
            <div className="w-32 shrink-0 overflow-hidden rounded-lg md:w-56" style={{ aspectRatio: "2 / 3" }}>
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - ${categoryLabel(movie.category)} - 97影院`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1 pt-1 text-background-50 md:pt-24">
              <nav className="mb-3 flex flex-wrap items-center gap-1.5 text-sm text-background-200">
                <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/category/${movie.category}`} className="transition-colors hover:text-accent-400">
                  {categoryLabel(movie.category)}
                </Link>
                <i className="ri-arrow-right-s-line" />
                <span className="text-background-50">{movie.title}</span>
              </nav>
              <h1 className="font-heading text-3xl font-black leading-tight md:text-4xl">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1 font-semibold text-accent-400">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="rounded bg-background-50/15 px-2 py-0.5">{movie.genre}</span>
                <span>{movie.region}</span>
                <span>{movie.year}</span>
                <span>{categoryLabel(movie.category)}</span>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-fill text-lg" />
                  立即播放（演示）
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-share-line text-base" />
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <div className="min-w-0 flex-1">
            <section>
              <h4 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500" />
                <a href="#intro" className="scroll-mt-24">剧情简介</a>
              </h4>
              <p id="intro" className="mt-4 text-sm leading-7 text-foreground-600">{movie.summary}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                {movie.title}是一部{movie.year}年上映的{movie.region}{movie.genre}作品，凭借扎实的叙事与出色的表演获得观众好评，
                当前综合评分 {movie.rating.toFixed(1)} 分。以下内容为演示示例，仅用于展示详情页信息结构。
              </p>
            </section>

            <section className="mt-10">
              <h4 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500" />
                <a href="#cast" className="scroll-mt-24">主演阵容</a>
              </h4>
              <div id="cast" className="mt-4 flex flex-wrap gap-3">
                {movie.cast.map((person) => (
                  <span
                    key={person}
                    className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700"
                  >
                    <i className="ri-user-3-line text-base text-secondary-500" />
                    {person}
                  </span>
                ))}
              </div>
            </section>

            {movie.episodes && movie.episodes.length > 0 && (
              <section className="mt-10">
                <h4 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="h-5 w-1.5 rounded-full bg-accent-500" />
                  <a href="#episodes" className="scroll-mt-24">剧集目录</a>
                </h4>
                <p className="mt-2 text-xs text-foreground-500">演示目录，点击可切换当前剧集信息。</p>
                <div id="episodes" className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {movie.episodes.map((ep) => (
                    <button
                      key={ep.index}
                      type="button"
                      onClick={() => setCurrentEpisode(ep.index)}
                      className={`flex items-center gap-2 rounded-md border px-3 py-3 text-left text-sm transition-colors ${
                        currentEpisode === ep.index
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          currentEpisode === ep.index
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {ep.index}
                      </span>
                      <span className="truncate">{ep.title}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-information-line mr-1.5" />
                  当前选中：
                  {movie.episodes.find((e) => e.index === currentEpisode)?.title ?? `第${currentEpisode}集`}
                  （演示内容）
                </div>
              </section>
            )}

            <section className="mt-10">
              <h4 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500" />
                <a href="#info" className="scroll-mt-24">作品信息</a>
              </h4>
              <dl id="info" className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {[
                  { label: "片名", value: movie.title },
                  { label: "类型", value: `${movie.category === "tv" ? "电视剧" : "电影"} / ${movie.genre}` },
                  { label: "制片地区", value: movie.region },
                  { label: "上映年份", value: `${movie.year}` },
                  { label: "综合评分", value: `${movie.rating.toFixed(1)} 分` },
                  { label: "更新状态", value: movie.category === "tv" ? "已完结（演示）" : "已上线（演示）" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between border-b border-background-200 py-2.5 text-sm">
                    <dt className="text-foreground-500">{row.label}</dt>
                    <dd className="font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>

          <aside className="w-full shrink-0 lg:w-80">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-lg text-accent-500" />
                热门榜单
              </h3>
              <ul className="mt-4 space-y-3">
                {getRelated(list, movie, 8).map((item, i) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex items-center gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold ${
                          i < 3 ? "bg-accent-500 text-primary-950" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="truncate text-sm text-foreground-700 group-hover:text-primary-600">{item.title}</span>
                      <span className="ml-auto shrink-0 text-xs text-foreground-400">{item.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <h4 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500" />
              <a href="#related" className="scroll-mt-24">相关推荐</a>
            </h4>
            <p className="mt-1.5 pl-3.5 text-sm text-foreground-500">根据同类型与热门程度为你推荐</p>
            <div id="related" className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      <ShareDialog open={shareOpen} title={movie.title} onClose={() => setShareOpen(false)} />
    </div>
  );
}